import { SolutionScenario } from '@/types/scenarios/solution';
import { Calendar, Phone, Users, Star, MapPin, Music, Clock, Ticket } from 'lucide-react';

export const events: SolutionScenario = {
  meta: {
    id: 'events',
    slug: 'events',
    name: 'Events',
    tagline: 'Genies that absorb the question flood so your team can run the show',
    description: "A genie that answers the same parking, dress-code, timing, and ticketing questions for the thousandth time so your event team can run the actual event, not the call centre.",
    market: 'Events',
    subMarkets: ['Conferences & Expos', 'Weddings & Private Events', 'Concerts & Live Shows', 'Corporate Events', 'Festivals & Fairs'],
    specificNeeds: [
      'Ticket & registration inquiries',
      'Event details & logistics',
      'Vendor coordination',
      'Attendee support',
      'VIP & guest management',
      'Day-of event assistance'
    ],
    commonRoles: ['Event Planners', 'Venue Managers', 'Production Coordinators', 'Guest Services'],
    marketSize: '$1.1 trillion global events industry',
    isFeatured: true
  },

  hero: {
    headline: 'Every guest question, handled',
    headlineGradient: 'Every guest question,',
    headlineRest: 'handled',
    subheadline: "A genie that answers the same parking, dress-code, timing, and ticketing questions for the thousandth time so your event team can run the actual event, not the call centre.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'ticket-support',
      name: 'Ticket & Registration Support',
      description: 'Handle ticket inquiries, pricing, availability, and group bookings around the clock',
      department: 'Sales',
      icon: Ticket,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        '24/7 ticket availability info',
        'Group booking assistance',
        'Pricing and package details',
        'Registration confirmation'
      ]
    },
    {
      id: 'event-info',
      name: 'Event Information Line',
      description: 'Provide instant answers about venue details, parking, schedule, dress code, and logistics',
      department: 'Guest Services',
      icon: MapPin,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Venue directions and parking',
        'Schedule and lineup details',
        'Dress code and FAQs',
        'Accessibility information'
      ]
    },
    {
      id: 'vendor-coordination',
      name: 'Vendor Coordination',
      description: 'Coordinate with caterers, AV teams, florists, and other vendors on logistics and timing',
      department: 'Operations',
      icon: Phone,
      playbookIds: ['appointment-scheduler', 'interactive-faq'],
      benefits: [
        'Load-in schedule management',
        'Setup requirement communication',
        'Day-of coordination',
        'Vendor contact routing'
      ]
    },
    {
      id: 'vip-services',
      name: 'VIP & Guest Services',
      description: 'Manage VIP lists, special requests, upgrades, and personalized guest experiences',
      department: 'Guest Services',
      icon: Star,
      playbookIds: ['interactive-faq', 'proactive-welcome'],
      benefits: [
        'VIP check-in and concierge',
        'Special request handling',
        'Upgrade and add-on booking',
        'Personalized event info'
      ]
    },
    {
      id: 'day-of-support',
      name: 'Day-Of Event Support',
      description: 'Real-time attendee assistance during events — directions, schedule changes, lost and found',
      department: 'Operations',
      icon: Clock,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Real-time schedule updates',
        'Wayfinding and directions',
        'Lost and found reporting',
        'Emergency information'
      ]
    },
    {
      id: 'post-event',
      name: 'Post-Event Follow-Up',
      description: 'Collect feedback, send thank-yous, and promote upcoming events to attendees',
      department: 'Marketing',
      icon: Music,
      playbookIds: ['lead-capture'],
      benefits: [
        'Automated feedback collection',
        'Thank-you messaging',
        'Future event promotion',
        'Attendee database building'
      ]
    }
  ],

  challenges: [
    {
      title: 'Attendee Volume Overwhelm',
      description: 'Hundreds or thousands of guests calling with questions before and during events — staff can\'t keep up',
      impact: 'high',
      affectedAreas: ['Guest experience', 'Staff burnout', 'Missed ticket sales']
    },
    {
      title: 'Last-Minute Changes',
      description: 'Schedule changes, venue updates, and logistics shifts need to be communicated instantly to attendees and vendors',
      impact: 'high',
      affectedAreas: ['Event execution', 'Guest confusion', 'Vendor coordination']
    },
    {
      title: 'Multi-Channel Inquiry Chaos',
      description: 'Inquiries come via phone, email, social media, and walk-ins — no single system handles them all',
      impact: 'medium',
      affectedAreas: ['Response consistency', 'Lead tracking', 'Staff efficiency']
    },
    {
      title: 'Day-Of Staff Overload',
      description: 'Event day staff are pulled in every direction — registration, directions, VIP requests, vendor issues — all at once',
      impact: 'high',
      affectedAreas: ['Event quality', 'Guest satisfaction', 'Staff stress']
    }
  ],

  solutions: [
    {
      challenge: 'Overwhelming attendee inquiry volume',
      solution: '24/7 Voice Genie handles ticket questions, event details, and logistics automatically',
      implementation: 'Deploy as the primary event information line and ticket inquiry handler',
      expectedResults: [
        '70% of attendee questions answered without staff',
        'Zero missed ticket inquiries',
        'Consistent, accurate event information'
      ]
    },
    {
      challenge: 'Last-minute changes and real-time communication',
      solution: 'Voice Genie provides real-time event updates to callers and can push schedule changes instantly',
      implementation: 'Update event knowledge base in real-time — Genie immediately reflects changes to all callers',
      expectedResults: [
        'Instant communication of schedule changes',
        '50% fewer confused attendees on event day',
        'Reduced social media complaints'
      ]
    },
    {
      challenge: 'Day-of staff overload',
      solution: 'Voice Genie handles routine attendee questions during events, freeing staff for high-touch needs',
      implementation: 'QR codes at venues for instant voice assistance — directions, schedule, food, restrooms',
      expectedResults: [
        '60% reduction in staff interruptions',
        'Faster attendee problem resolution',
        'Staff focused on VIP and critical issues'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'Eventbrite',
        category: 'Ticketing',
        description: 'Event ticketing and registration platform',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Cvent',
        category: 'Event Management',
        description: 'Enterprise event management platform',
        required: false,
        popularityScore: 90
      },
      {
        name: 'Social Tables',
        category: 'Event Design',
        description: 'Event diagramming and seating management',
        required: false,
        popularityScore: 82
      },
      {
        name: 'Ticketmaster',
        category: 'Ticketing',
        description: 'Large-scale event ticketing platform',
        required: false,
        popularityScore: 88
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Attendee and sponsor relationship management',
        required: false,
        popularityScore: 92
      },
      {
        name: 'Slack',
        category: 'Communication',
        description: 'Team coordination and vendor communication',
        required: false,
        popularityScore: 88
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '320%',
    adoptionRate: '88% attendee satisfaction',
    customerSatisfaction: '4.7/5'
  },

  demoConfig: {
    firstMessage: "Hi! I'm your event assistant. I can help with ticket information, event details, directions, scheduling, or anything else you need for your event. How can I help?",
    systemPrompt: "You are a friendly and efficient event assistant. Help callers with ticket inquiries, event logistics, venue details, vendor coordination, and attendee support. Be upbeat and helpful — events are exciting! Provide accurate information about schedules, pricing, parking, and any event-specific details. For vendor calls, be professional and detail-oriented about load-in times and setup requirements.",
    marketContext: "Event planning company or venue managing conferences, weddings, concerts, and corporate events",
    exampleScenarios: [
      "How much are tickets for the Saturday night show?",
      "What time do doors open and where do I park?",
      "I'm a vendor — what time is load-in on Friday?",
      "We'd like to book your venue for a corporate event"
    ]
  },

  caseStudies: [
    {
      companyName: 'Horizon Events Group',
      companySize: '200+ events per year',
      location: 'Nashville, Tennessee',
      challenge: 'Staff overwhelmed by attendee calls before and during events, missing ticket sales, and unable to coordinate vendors efficiently during setup',
      implementation: 'Deployed Help Genie as the primary event info line, vendor coordination assistant, and day-of attendee support via QR codes at venues',
      timeline: '2 weeks',
      results: [
        {
          metric: 'Ticket Inquiries Handled',
          value: '85%',
          improvement: 'Automated without staff'
        },
        {
          metric: 'Staff Time Saved',
          value: '4 hrs/event',
          improvement: 'Average per event'
        },
        {
          metric: 'Attendee Satisfaction',
          value: '4.8/5',
          improvement: 'Up from 4.1'
        },
        {
          metric: 'Ticket Revenue',
          value: '+22%',
          improvement: 'From captured inquiries'
        }
      ],
      quote: {
        text: "On event day, our team used to be buried in calls about parking and schedules. Now our Genie handles all of that, and our staff can focus on making the event incredible. We've even seen ticket sales go up because inquiries actually get answered now.",
        author: 'Marcus Bell',
        title: 'Director of Operations'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Event Voice Assistant Playbook',
      description: 'How event companies use voice AI to streamline operations and delight attendees',
      url: '/resources/event-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Scaling Events Without Scaling Staff',
      description: 'How venues and planners handle 10x attendee volume with voice AI',
      url: '/webinars/event-voice-ai'
    },
    {
      type: 'template',
      title: 'Event Day Support Scripts',
      description: 'Pre-built voice scripts for ticket support, venue info, and vendor coordination',
      url: '/templates/event-scripts'
    }
  ],

  faqs: [
    {
      question: 'Can the Genie handle event-day changes in real-time?',
      answer: 'Yes. When you update the event schedule or details in the knowledge base, every caller immediately gets the latest information. If a performer is delayed or a room changes, your Genie communicates the update to everyone who calls.'
    },
    {
      question: 'How do attendees access the Genie during an event?',
      answer: 'Place QR codes throughout your venue — at entrances, stages, food areas, and restrooms. Attendees scan with their phone and instantly connect to your Genie for directions, schedule info, or help. No app download needed.'
    },
    {
      question: 'Can it handle different types of events — weddings, conferences, and concerts?',
      answer: 'Absolutely. You can configure your Genie with event-specific knowledge for each event you run. Whether it\'s a black-tie wedding, a tech conference, or a music festival, the Genie adapts its tone and information to match.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "A thousand guests, one Genie\nevery question answered",
      subtitle: "See how your Genie handles real event scenarios — selling tickets, coordinating vendors, and supporting attendees before, during, and after the big day.",
    },
    chatScenarios: [
      {
        id: 'ticket_inquiry',
        title: 'Ticket Inquiry',
        messages: [
          { type: 'user' as const, text: "Hi, I'm looking at tickets for the Summer Music Fest next month. What's still available?", delay: 300 },
          { type: 'genie' as const, text: "Great choice! Summer Music Fest is August 15-17. We still have General Admission at $89, VIP at $199 which includes front-stage access and the VIP lounge, and a limited number of Platinum passes at $349 with backstage meet-and-greet. How many tickets are you looking for?", delay: 1200 },
          { type: 'user' as const, text: "Four VIP tickets please. Is there a group discount?", delay: 2500 },
          { type: 'genie' as const, text: "Yes! Groups of 4 or more get 10% off — so your four VIP tickets would be $716 instead of $796. That includes front-stage access, the VIP lounge with complimentary drinks, and dedicated restrooms. Shall I reserve those for you?", delay: 3400 },
          { type: 'user' as const, text: "That's awesome. Yes, reserve them under Jennifer Park.", delay: 4600 },
          { type: 'genie' as const, text: "Done! Four VIP tickets reserved for Jennifer Park — Summer Music Fest, August 15-17. I'll send a confirmation with your e-tickets, parking info, and the VIP entrance location. You're going to have an amazing time!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Ticket options and pricing provided', delay: 6500 },
          { id: '2', text: 'Group discount applied (10% off)', delay: 6700 },
          { id: '3', text: '4 VIP tickets reserved ($716)', delay: 6900 },
          { id: '4', text: 'Confirmation and details sent', delay: 7100 },
          { id: '5', text: 'Revenue captured after hours', delay: 7300 },
        ],
      },
      {
        id: 'wedding_planning',
        title: 'Wedding Inquiry',
        messages: [
          { type: 'user' as const, text: "Hi, my fiance and I are getting married next October and we're looking at venues. Do you have availability for about 150 guests?", delay: 300 },
          { type: 'genie' as const, text: "Congratulations! October is a beautiful time for a wedding. We have two dates open in October for 150 guests — October 11th and October 25th. Our Grand Ballroom seats up to 200 comfortably. Would you like to hear about our wedding packages?", delay: 1200 },
          { type: 'user' as const, text: "October 11th could work! What's included in your packages?", delay: 2500 },
          { type: 'genie' as const, text: "Our Signature package at $18,500 includes the venue for ceremony and reception, in-house catering for 150, a bridal suite, tables and linens, and a dedicated event coordinator. We also have a Premium package at $24,000 that adds a cocktail hour, upgraded bar, and floral centerpieces.", delay: 3400 },
          { type: 'user' as const, text: "The Premium sounds wonderful. Can we schedule a venue tour?", delay: 4600 },
          { type: 'genie' as const, text: "Absolutely! I have openings this Thursday at 4 PM or Saturday at 11 AM for a private venue tour. Our wedding coordinator Emily will walk you through both spaces and discuss customization. Which works better for you?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Date availability confirmed', delay: 6500 },
          { id: '2', text: 'Package options and pricing shared', delay: 6700 },
          { id: '3', text: 'Venue tour scheduled', delay: 6900 },
          { id: '4', text: 'Wedding coordinator assigned', delay: 7100 },
          { id: '5', text: 'High-value lead captured ($24K+)', delay: 7300 },
        ],
      },
      {
        id: 'corporate_event',
        title: 'Corporate Booking',
        messages: [
          { type: 'user' as const, text: "I'm looking to book a space for our company's annual kickoff — about 300 people, full day event with breakout sessions.", delay: 300 },
          { type: 'genie' as const, text: "We'd love to host your kickoff! For 300 attendees with breakout sessions, our Conference Center is ideal — it has a main hall for 400 plus four breakout rooms for 50 each. When are you targeting?", delay: 1200 },
          { type: 'user' as const, text: "January 15th. We'd need AV setup, catering, and a stage for our CEO's presentation.", delay: 2500 },
          { type: 'genie' as const, text: "January 15th is available. Our corporate package includes full AV with a 20-foot screen and wireless mics, a raised stage, all-day catering with breakfast, lunch, and afternoon snacks, plus WiFi for all attendees. For 300 people, that comes to $145 per person.", delay: 3400 },
          { type: 'user' as const, text: "That's within our budget. Can we add an evening cocktail reception too?", delay: 4600 },
          { type: 'genie' as const, text: "Of course! The cocktail reception adds $45 per person and includes passed hors d'oeuvres and an open bar for two hours. I'll have our corporate events manager send you a detailed proposal by tomorrow. Can I get your email and company name?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Venue and date availability confirmed', delay: 6500 },
          { id: '2', text: 'AV and catering requirements captured', delay: 6700 },
          { id: '3', text: 'Corporate package quoted ($43,500+)', delay: 6900 },
          { id: '4', text: 'Cocktail reception upsell ($13,500)', delay: 7100 },
          { id: '5', text: 'Proposal follow-up scheduled', delay: 7300 },
        ],
      },
      {
        id: 'day_of_support',
        title: 'Day-Of Support',
        messages: [
          { type: 'user' as const, text: "Hey, I'm at the food festival and I can't find the craft beer tent. Also, what time does the live music start?", delay: 300 },
          { type: 'genie' as const, text: "Welcome to the festival! The craft beer tent is in Zone C — from the main entrance, head straight past the food trucks and turn right at the big oak tree. You can't miss the blue and white tent! Live music starts at 4 PM on the Main Stage in Zone A.", delay: 1200 },
          { type: 'user' as const, text: "Perfect. Is there a schedule somewhere? I don't want to miss the headliner.", delay: 2500 },
          { type: 'genie' as const, text: "The headliner — The Riverside Band — goes on at 8:30 PM on the Main Stage. Here's the lineup: Local acts from 4-6 PM, the DJ set from 6-7:30 PM, opening act at 7:30 PM, and headliner at 8:30 PM. I'd recommend grabbing a spot by 8 PM — it'll be packed!", delay: 3400 },
          { type: 'user' as const, text: "Awesome, thanks! One more thing — are dogs allowed?", delay: 4600 },
          { type: 'genie' as const, text: "Yes! Well-behaved dogs on leashes are welcome in all outdoor zones. There's even a dog water station near the craft beer tent in Zone C. Enjoy the festival!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Wayfinding directions provided', delay: 6500 },
          { id: '2', text: 'Full music schedule shared', delay: 6700 },
          { id: '3', text: 'Pet policy answered instantly', delay: 6900 },
          { id: '4', text: 'Zero staff involvement needed', delay: 7100 },
          { id: '5', text: 'Attendee experience enhanced', delay: 7300 },
        ],
      },
      {
        id: 'vendor_coordination',
        title: 'Vendor Call',
        messages: [
          { type: 'user' as const, text: "This is Tony from Bella Catering. We're doing the food for the Henderson wedding Saturday. What time is load-in and where do we park the truck?", delay: 300 },
          { type: 'genie' as const, text: "Hi Tony! For the Henderson wedding on Saturday, vendor load-in starts at 10 AM through the service entrance on the east side of the building. There's a designated loading zone right by the kitchen door — pull in from Oak Street.", delay: 1200 },
          { type: 'user' as const, text: "Got it. How many guests are we feeding and is the final count confirmed?", delay: 2500 },
          { type: 'genie' as const, text: "Final count is confirmed at 142 guests. Dinner service is at 6:30 PM following cocktail hour at 5:30 PM. The kitchen is fully available from 10 AM. Do you need access to our ovens and prep tables, or are you bringing everything?", delay: 3400 },
          { type: 'user' as const, text: "We'll use your ovens. Can we get the kitchen walkthrough at 10 when we arrive?", delay: 4600 },
          { type: 'genie' as const, text: "Absolutely. I've noted a kitchen walkthrough for Bella Catering at 10 AM Saturday. Our venue coordinator Sarah will meet you at the service entrance. Is there anything else you need — extra tables, power outlets, refrigerator space?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Load-in time and location confirmed', delay: 6500 },
          { id: '2', text: 'Final guest count provided (142)', delay: 6700 },
          { id: '3', text: 'Kitchen access arranged', delay: 6900 },
          { id: '4', text: 'Venue coordinator assigned', delay: 7100 },
          { id: '5', text: 'Vendor fully briefed without planner', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Run Bigger Events With Less Stress",
      subtitle: "Your Genie handles the flood of attendee questions, vendor calls, and ticket inquiries — so your team can focus on creating unforgettable experiences.",
    },
    useCaseCards: [
      {
        id: 'ticket-registration',
        title: 'Ticket & Registration',
        description: "Every ticket inquiry answered instantly — pricing, availability, group discounts, VIP upgrades. Your Genie sells tickets and handles registration while you sleep.",
        bullets: [
          'Real-time ticket availability',
          'Group discount calculations',
          'VIP and upgrade options',
          'Registration confirmation sent instantly',
        ],
        image: '/solutions-pages/events/usecase-ticket-registration.webp',
      },
      {
        id: 'event-logistics',
        title: 'Event Logistics & Info',
        description: "Parking, directions, schedule, dress code, accessibility — your Genie answers the same 50 questions so your staff doesn't have to.",
        bullets: [
          'Venue directions and parking info',
          'Real-time schedule and lineup',
          'Accessibility and accommodation details',
          'Weather and preparation updates',
        ],
        image: '/solutions-pages/events/usecase-event-logistics.webp',
      },
      {
        id: 'vip-guest-services',
        title: 'VIP & Guest Services',
        description: "VIP check-in, special requests, dietary needs, seating preferences — your Genie provides concierge-level service to every guest.",
        bullets: [
          'VIP list management and check-in',
          'Special request handling',
          'Dietary and accessibility needs',
          'Personalized event information',
        ],
        image: '/solutions-pages/events/usecase-vip-services.webp',
      },
      {
        id: 'vendor-management',
        title: 'Vendor Coordination',
        description: "Load-in times, setup details, guest counts, kitchen access — vendors get the info they need without calling the planner during crunch time.",
        bullets: [
          'Load-in schedules and logistics',
          'Final counts and setup requirements',
          'Venue access coordination',
          'Day-of vendor support line',
        ],
        image: '/solutions-pages/events/usecase-vendor-coordination.webp',
      },
    ],
    finalCTA: {
      title: "Your Events Deserve a Genie",
      subtitle: "Before the doors open, while the crowd is roaring, and after the last guest leaves — your Genie handles every question, every ticket, and every detail so you can focus on the magic.",
      pillars: [
        {
          title: 'Always\nSelling',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Tickets sold, registrations processed, and upsells offered — 24/7.',
        },
        {
          title: 'Always\nCoordinating',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Vendors briefed, schedules shared, and logistics handled — without you.',
        },
        {
          title: 'Always\nDelighting',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Every attendee question answered instantly — from parking to the headliner.',
        },
      ],
      backgroundImage: '/solutions-pages/events/cta-background.webp',
    },
  }
};
